import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

const MpesaConfig = () => {
  const [loading, setLoading] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [configData, setConfigData] = useState({
    consumer_key: '',
    consumer_secret: '',
    passkey: '',
    shortcode: '',
    environment: 'sandbox',
    callback_url: '',
    initiator_name: '',
    security_credential: '',
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await api.get('/api/config/mpesa');
      console.log('M-Pesa Config Response:', response.data);
      
      if (response.data && response.data.success) {
        const config = response.data.data;
        setIsConfigured(config.configured || false);
        
        // Populate form with backend data
        setConfigData(prev => ({
          ...prev,
          environment: config.environment || 'sandbox',
          shortcode: config.shortcode && config.shortcode !== 'Not configured' ? config.shortcode : prev.shortcode,
          // For masked sensitive fields, show placeholder if configured
          consumer_key: config.consumer_key && config.consumer_key !== 'Not configured' ? '••••••••••••' : prev.consumer_key,
          consumer_secret: config.consumer_secret && config.consumer_secret !== 'Not configured' ? '••••••••••••' : prev.consumer_secret,
          passkey: config.passkey && config.passkey !== 'Not configured' ? '••••••••••••' : prev.passkey,
        }));
      }
    } catch (error) {
      console.error('Failed to fetch M-Pesa config:', error);
      toast.error('Failed to load M-Pesa configuration');
    }
  };

  const handleChange = (e) => {
    setConfigData({
      ...configData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/config/mpesa', configData);
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'M-Pesa configuration validated successfully');
        
        // Show important warning about .env file
        if (response.data.warning) {
          toast.warning(response.data.warning, { autoClose: 8000 });
          console.log('M-Pesa Configuration Instructions:', response.data.instructions);
          console.log('Note:', response.data.note);
        }
        
        // Fetch config again to update the form (though sensitive values will be masked)
        await fetchConfig();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save M-Pesa configuration');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const testConnection = async () => {
    if (!isConfigured) {
      toast.error('Please configure M-Pesa settings first');
      return;
    }
    
    setLoading(true);
    try {
      // Test connection uses backend-stored credentials
      const response = await api.post('/api/config/mpesa/test');
      
      if (response.data && response.data.success) {
        toast.success(response.data.message || 'M-Pesa connection test successful');
      } else {
        toast.error(response.data?.message || response.data?.error || 'M-Pesa connection test failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || 'M-Pesa connection test failed';
      toast.error(errorMsg);
      console.error('Test connection error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <PaymentIcon color="primary" />
        <Typography variant="h6">M-Pesa Payment Configuration</Typography>
      </Box>
      <Divider sx={{ mb: 3 }} />

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Environment</InputLabel>
              <Select
                name="environment"
                value={configData.environment}
                onChange={handleChange}
                label="Environment"
              >
                <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                <MenuItem value="production">Production (Live)</MenuItem>
              </Select>
              <FormHelperText>
                Use sandbox for testing, production for live transactions
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Consumer Key"
              name="consumer_key"
              type="password"
              value={configData.consumer_key}
              onChange={handleChange}
              required
              helperText="Daraja API Consumer Key"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Consumer Secret"
              name="consumer_secret"
              type="password"
              value={configData.consumer_secret}
              onChange={handleChange}
              required
              helperText="Daraja API Consumer Secret"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Shortcode"
              name="shortcode"
              value={configData.shortcode}
              onChange={handleChange}
              required
              helperText="M-Pesa Paybill/Till Number"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Passkey"
              name="passkey"
              type="password"
              value={configData.passkey}
              onChange={handleChange}
              required
              helperText="Lipa Na M-Pesa Online Passkey"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Callback URL"
              name="callback_url"
              value={configData.callback_url}
              onChange={handleChange}
              required
              helperText="URL to receive payment notifications"
              placeholder="https://yourdomain.com/api/mpesa/callback"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Initiator Name"
              name="initiator_name"
              value={configData.initiator_name}
              onChange={handleChange}
              helperText="For B2C and Reversal transactions (optional)"
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Security Credential"
              name="security_credential"
              type="password"
              value={configData.security_credential}
              onChange={handleChange}
              helperText="Encrypted initiator password (optional)"
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" gap={2}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                Save Configuration
              </Button>
              <Button
                variant="outlined"
                onClick={testConnection}
                disabled={loading}
              >
                Test Connection
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default MpesaConfig;
